import React, {
    FormEvent,
    useCallback,
    useEffect,
    useLayoutEffect,
} from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from 'app/state/types'
import { useNavigate, useParams } from 'react-router-dom'
import { getApp, updateAppService } from 'app/services/client'
import { updateApp, setApp, setIsLoading } from 'app/state/appSlice'
import { APP_ROUTES } from 'app/components/AppRouter/routes'
import {
    isTokenExpired,
    onFileInputChange,
    onInputChange,
    onSelectChange,
} from 'app/utils/hooks'
import { AppEditableForm } from './AppEditableForm'
import {
    makeTokenHeaderConfig,
    useTokenHeaderConfig,
} from 'app/services/tokenConfig'
import { handleServerAPIError } from 'app/services/utils'

export const EditForm = () => {
    const { app } = useSelector((state: RootState) => state.appSlice)
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const { id } = useParams<{ id: string }>()
    const [token] = useTokenHeaderConfig()

    const onFormSubmit = useCallback(
        (event: FormEvent<HTMLFormElement>) => {
            event.preventDefault()

            if (id && app) {
                dispatch(setIsLoading(true))
                updateAppService(id, app, makeTokenHeaderConfig(token))
                    .then(() => {
                        navigate(APP_ROUTES.APP_DETAILS.replace(':id', id))
                    })
                    .catch(e => handleServerAPIError(e, dispatch, navigate))
                    .finally(() => dispatch(setIsLoading(false)))
            } else {
                console.warn('Object App is empty')
            }
        },
        [dispatch, navigate, app],
    )

    const onAppChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) =>
            onInputChange(e)(dispatch, updateApp),
        [updateApp, onInputChange, dispatch],
    )

    const onAppFileChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) =>
            onFileInputChange(e)(dispatch, updateApp),
        [updateApp, onFileInputChange, dispatch],
    )

    const onAppSelectChange = useCallback(
        (e: React.ChangeEvent<HTMLSelectElement>) =>
            onSelectChange(e)(dispatch, updateApp),
        [updateApp, onSelectChange, dispatch],
    )

    useLayoutEffect(() => {
        if (id && !isTokenExpired()) {
            dispatch(setIsLoading(true))
            getApp(id)
                .then(res => dispatch(setApp(res.data)))
                .catch(e => handleServerAPIError(e, dispatch, navigate))
                .finally(() => dispatch(setIsLoading(false)))
        }
    }, [])

    useEffect(() => {
        if (isTokenExpired()) navigate(APP_ROUTES.LOGIN)
    }, [])

    return (
        <AppEditableForm
            app={app}
            onSubmit={onFormSubmit}
            onAppChange={onAppChange}
            onAppSelectChange={onAppSelectChange}
            onAppFileChange={onAppFileChange}
            isEdit
        />
    )
}
